import type { ColumnDef } from '@/lib/tanstack-table';
import type { AgentSopSummary } from '@erp/rpc';
import type { useGT } from 'gt-react';
import { Badge } from 'src/components/ui/badge';
import { siteOf } from './procedureDisplay';
import { ProcedureNameCell } from './ProcedureNameCell';

/**
 * The Name / Site / Version columns shared by the main Procedures list and the agent's Procedures
 * tab. Both render the same rows through the same DataTable, so the columns live here once and each
 * page adds only what is specific to it (the tab's allow-checkbox, params and Open action).
 *
 * `gt` is passed in rather than called here because header strings must be translated in the
 * component that owns the memo, keeping the columns stable across renders.
 */
type Gt = ReturnType<typeof useGT>;

export function procedureNameColumn(gt: Gt): ColumnDef<AgentSopSummary> {
  return {
    accessorKey: 'title',
    header: gt('Name', { context: 'procedures table column' }),
    cell: ({ row }) => <ProcedureNameCell sop={row.original} />,
  };
}

export function procedureSiteColumn(gt: Gt): ColumnDef<AgentSopSummary> {
  return {
    accessorKey: 'startUrl',
    header: gt('Site', { context: 'procedures table column' }),
    cell: ({ row }) => {
      const site = siteOf(row.original);
      return site ? (
        <span className="text-sm text-muted-foreground">{site}</span>
      ) : (
        <span className="text-sm text-muted-foreground" aria-hidden>
          —
        </span>
      );
    },
  };
}

export function procedureVersionColumn(gt: Gt): ColumnDef<AgentSopSummary> {
  return {
    accessorKey: 'version',
    header: gt('Version', { context: 'procedures table column' }),
    cell: ({ row }) => (
      <Badge variant="outline" className="font-mono text-xs">
        {`v${row.original.version}`}
      </Badge>
    ),
  };
}
